import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Announcement, AnnouncementService, AnnouncementType } from '../../../core/services/announcement.service';

/**
 * The paged announcement dialog.
 *
 * `tasks/announcements.md` — Phase F1.
 *
 * ── WHAT IS ACTUALLY RECORDED, AND WHEN ──────────────────────────────────────────────
 *
 * Only the pages the user REACHED are marked `seen` when the dialog closes. Marking all of them
 * would let paging away from page 2 silently bury page 3 — the user would have "seen" something
 * they never laid eyes on, and after two of those it would stop being offered at all.
 *
 * `Dismiss` applies to the CURRENT page only. That is what the button says, and it is what an
 * author would expect: "don't show me this one again" is not "don't show me any of these again".
 *
 * Closing with `Esc`, the backdrop or the Close button is a deliberate NON-answer, so it records
 * `seen` rather than `dismissed` and the announcement is offered once more. The server stops
 * offering it after two such passes.
 *
 * ── THE ONE BLOCKING CASE ────────────────────────────────────────────────────────────
 *
 * A `critical` announcement is opened with `disableClose` by the caller, and this component
 * replaces every action with a single acknowledgement. There is deliberately no way to page past
 * an un-acknowledged critical notice onto something else.
 */
@Component({
    selector: 'app-announcement-dialog',
    templateUrl: './announcement-dialog.component.html',
    styleUrls: ['./announcement-dialog.component.scss'],
})
export class AnnouncementDialogComponent implements OnDestroy {

    announcements: Announcement[] = [];
    index = 0;

    /**
     * Ids of pages the user actually landed on. Page 0 counts from the moment the dialog opens —
     * it is on screen.
     */
    private reached = new Set<string>();

    /** Pages already answered explicitly, so closing does not overwrite the answer with `seen`. */
    private answered = new Set<string>();

    constructor(
        public dialogRef: MatDialogRef<AnnouncementDialogComponent>,
        private announcementService: AnnouncementService,
        @Inject(MAT_DIALOG_DATA) public data: { announcements: Announcement[] },
    ) {
        this.announcements = (data && data.announcements) || [];
        if (this.announcements.length) {
            this.reached.add(this.announcements[0].id);
        }

        // Covers Esc and the backdrop as well as the Close button — every path out of the dialog
        // lands here, so there is one place that decides what a non-answer records.
        this.dialogRef.afterClosed().subscribe(() => this.recordReached());
    }

    get current(): Announcement {
        return this.announcements[this.index];
    }

    iconFor(type: AnnouncementType): string {
        switch (type) {
            case 'critical': return 'gpp_maybe';
            case 'release': return 'auto_awesome';
            default: return 'info';
        }
    }

    next(): void {
        if (this.index >= this.announcements.length - 1) return;
        this.index += 1;
        this.reached.add(this.current.id);
    }

    previous(): void {
        if (this.index <= 0) return;
        this.index -= 1;
    }

    snooze(): void {
        const id = this.current.id;
        this.answered.add(id);
        this.announcementService.snooze(id).subscribe();
        this.advanceOrClose();
    }

    dismiss(): void {
        const id = this.current.id;
        this.answered.add(id);
        this.announcementService.dismiss(id).subscribe();
        this.advanceOrClose();
    }

    acknowledge(): void {
        const id = this.current.id;
        this.answered.add(id);
        this.announcementService.acknowledge(id).subscribe();
        this.advanceOrClose();
    }

    close(): void {
        this.dialogRef.close();
    }

    /**
     * After answering, move to the next unanswered page rather than closing outright — a backlog
     * of three should take one pass, not three openings of the same dialog.
     */
    private advanceOrClose(): void {
        const remaining = this.announcements.findIndex(
            (a, i) => i !== this.index && !this.answered.has(a.id));
        if (remaining === -1) {
            this.dialogRef.close();
            return;
        }
        this.index = remaining;
        this.reached.add(this.current.id);
    }

    /** Marks every page the user reached but did not answer. Called once, on close. */
    private recordReached(): void {
        this.reached.forEach((id) => {
            if (!this.answered.has(id)) {
                this.announcementService.markSeen(id).subscribe();
            }
        });
        this.reached.clear();
    }

    ngOnDestroy(): void {
        // `afterClosed` has normally already fired; this covers a destroy that bypasses it (a route
        // change tearing the dialog down), so a page the user genuinely saw is not lost.
        this.recordReached();
    }
}
